import { GLOBAL } from '@/store/types'
import router from '@/router'
import utils from '@/utils/utils'
import strings from '@/strings'
import settingService from '@/api/setting'
import storage from '@/utils/storage'
import EventBus from '@/eventbus/index.js'

const Global = {
  namespaced: true,
  state: {
    message: '',
    messageValue: null,
    translated: false,
    loading: false,
    shouldHideLoading: true,
    initialSettingsLoaded: false,
    windowWidth: 0,
    blockDialog: false,
    blockDialogTerms: false,
    corporateNoticeDisplayed: false,
    initialSettings: {
      desks: {},
      restaurants: {},
      charteredBuses: {},
      parkings: {},
      rooms: {},
      lockers: {},
      hasMonitoring: false,
      hasAccessControl: false,
      hasComfort: false,
      corporateNotice: {},
      applicationColor: null
    },
    blockDialogNPS: false
  },
  getters: {
    message (state) {
      return state.message !== ''
    },
    isMobile (state) {
      return state.windowWidth < 961
    },
    blockDialog (state) {
      return state.blockDialog
    },
    blockDialogTerms (state) {
      return state.blockDialogTerms
    },
    corporateNoticeDisplayed (state) {
      return state.corporateNoticeDisplayed
    },
    hasServiceDesk (state) {
      return state.initialSettings.hasServiceDeskRequests
    },
    hasBookingForGuests (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.hasBookingForGuests
    },
    hasBookingForRestaurant (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.restaurants.hasBooking
    },
    hasBookingForChartered (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.charteredBuses.hasBooking
    },
    hasBookingForParking (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.parkings.hasBooking
    },
    hasBookingForLocker (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.lockers.hasBooking
    },
    hasBookingForDesk (state) {
      return state.initialSettings.hasWorkspace && state.initialSettings.desks.hasBooking
    },
    hasContactForm (state) {
      return state.initialSettings.hasContactForm
    },
    hasLogo (state) {
      return state.initialSettings.hasLogo
    },
    featureFlags (state) {
      return state.initialSettings.featureFlags
    },
    settings (state) {
      return state.settings
    },
    //To build book menu
    hasBookingFor(state, getters){
      return {
        'desks': getters.hasBookingForDesk,
        'team': getters.hasBookingForDesk,
        'rooms': state.initialSettings.hasMeetingSpace,
        'roomNow': state.initialSettings.hasMeetingSpace,
        'restaurant': getters.hasBookingForRestaurant,
        'charteredBuses': getters.hasBookingForChartered,
        'parking': getters.hasBookingForParking,
        'lockers': getters.hasBookingForLocker
      }
    },
    //Check product with meta feature on route
    hasProduct (state, getters) {
      return {
        hasBookingForDesk: state.initialSettings.desks.hasBooking,
        hasBookingForRestaurant: state.initialSettings.restaurants.hasBooking,
        hasBookingForChartered: state.initialSettings.charteredBuses.hasBooking,
        hasBookingForParking: state.initialSettings.parkings.hasBooking,
        hasBookingForLocker: state.initialSettings.lockers.hasBooking,
        hasBookingFeatures: getters.hasBookingFeatures,
        hasBookingForRoom: state.initialSettings.hasMeetingSpace,
        hasMonitoring: state.initialSettings.desks.hasMonitoring,
        hasComfort: state.initialSettings.hasComfort,
        hasWayfinding: state.initialSettings.desks.hasWayfinding
      }
    },
    hasAccessControl (state) {
      return state.initialSettings.hasAccessControl
    },
    hasComfort (state) {
      return state.initialSettings.hasComfort
    },
    hasBookingForRoom(state) {
      return state.initialSettings.hasMeetingSpace
    },
    hasMonitoring (state) {
      if (!state.initialSettings.hasWorkspace) {
        return false
      }
      return state.initialSettings.desks ? state.initialSettings.desks.hasMonitoring : false
    },
    hasFrMonitoring (state) {
      if (!state.initialSettings.hasMeetingSpace) {
        return false
      }
      return state.initialSettings.rooms ? state.initialSettings.rooms.hasMonitoring : false
    },
    hasFrReportProcessing (state) {
      if (!state.initialSettings.hasMeetingSpace) {
        return false
      }
      return state.initialSettings.rooms ? state.initialSettings.rooms.reportProcessingEnabled : false
    },
    hasBookingFeatures (state) {
      return state.initialSettings.desks ? (
        state.initialSettings.hasMeetingSpace ||
        state.initialSettings.desks.hasBooking ||
        state.initialSettings.restaurants.hasBooking ||
        state.initialSettings.charteredBuses.hasBooking ||
        state.initialSettings.parkings.hasBooking ||
        state.initialSettings.lockers.hasBooking) : false
    },
    isServiceDeskEnabled (state) {
      return state.initialSettings.rooms ? state.initialSettings.rooms.isServiceDeskEnabled : false
    },
    hasValidInitialSettings (state) {
      const dmUserConfig = state.initialSettings.user.workspace
      const frUserConfig = state.initialSettings.user.meetingSpace

      if(dmUserConfig){
        if(!dmUserConfig.favoriteSiteId) { return false }
        if(!dmUserConfig.favoriteLocationId) { return false }
        if(!dmUserConfig.startWorkingTime) { return false }
        if(!dmUserConfig.finishWorkingTime) { return false }
        if(state.initialSettings.desks.hasPinCheckIn && !dmUserConfig.pin) { return false }
      }

      if(frUserConfig){
        if(!frUserConfig.favoriteLocationId) { return false }
        if(state.initialSettings.rooms.hasPinCheckIn && !frUserConfig.pin) { return false }
      }

      if(state.initialSettings.hasCostCenter && !state.initialSettings.user.costCenter) { return false }

      return true
    },
    hasWorkspace(state) {
      return state.initialSettings.hasWorkspace
    },
    hasMeetingSpace(state) {
      return state.initialSettings.hasMeetingSpace
    },
    blockDialogNPS(state){
      return state.blockDialogNPS
    },
    isTeamBookingOnlyByTeamManagers(state) {
      return state.initialSettings.desks.isTeamBookingOnlyByTeamManagers
    }
  },
  mutations: {
    [GLOBAL.SET_INITIAL_SETTINGS] (state, initialSettings) {
      state.initialSettings = initialSettings
    },
    [GLOBAL.SET_MESSAGE] (state, value) {
      if(typeof(value) === "string"){
        state.message = value
        state.messageValue = []
      } else {
        state.translated = value.translated
        state.message = value.message
        state.messageValue = value.messageValue
      }
    },
    [GLOBAL.RESET_MESSAGE] (state) {
      state.message = ''
      state.messageValue = null
      state.translated = false
    },
    [GLOBAL.INITIAL_SETTINGS_LOADED] (state, value) {
      state.initialSettingsLoaded = value
      EventBus.publish("initialSettingsLoaded")
    },
    [GLOBAL.SET_WINDOW_WIDTH] (state, value) {
      state.windowWidth = value
    },
    [GLOBAL.SET_LOADING] (state, value) {
      if(value){
        state.shouldHideLoading = false
        state.loading = value
      } else {
        state.shouldHideLoading = true
        setInterval(()=>{
          if(state.shouldHideLoading){
            state.loading = value
          }
        },500)
      }
    },
    [GLOBAL.RESET_COLOR] (state){
      state.initialSettings.applicationColor = {
        'primary': strings.primaryColor,
        'accent': strings.accentColor
      }
    },
    [GLOBAL.SET_BLOCK_DIALOG] (state, show){
      state.blockDialog = show
    },
    [GLOBAL.SET_BLOCK_DIALOG_TERMS] (state, show){
      state.blockDialogTerms = show
    },
    [GLOBAL.SET_CORPORATE_NOTICE_DISPLAYED] (state, value){
      state.corporateNoticeDisplayed = value
    },
    [GLOBAL.SET_BLOCK_DIALOG_NPS] (state, show){
      state.blockDialogNPS = show
    }
  },
  actions: {
    getInitialSettings: function ({commit, dispatch}, clientId) {
      dispatch('Global/showLoading', null, {root: true})
      return new Promise((resolve, reject) => {
        settingService.getInitialSettings(clientId).then((response) => {
          commit(GLOBAL.SET_INITIAL_SETTINGS, response.data)
          storage.saveToLocalStorage('settings', response.data)
          dispatch('Global/setInitialSettingsLoaded', true, {root: true})
          resolve()
        }).catch((error) => {
          reject(error)
        })
      })
    },
    showMessage ({commit}, obj) {
      commit(GLOBAL.SET_MESSAGE, obj)
    },
    hideMessage ({commit}) {
      commit(GLOBAL.RESET_MESSAGE)
    },
    resetColor({commit}) {
      commit(GLOBAL.RESET_COLOR)
    },
    hasFeatureForRoute({getters}, path){
      for (let route of router.options.routes) {
        if (route.path === path) {
          return utils.checkFeatureInRoute(route.meta.features, getters.hasProduct)
        }
      }
      return false
    },
    setWindowWidth ({commit}, value) {
      commit(GLOBAL.SET_WINDOW_WIDTH, value)
    },
    showLoading ({commit}) {
      commit(GLOBAL.SET_LOADING, true)
    },
    hideLoading ({commit}) {
      commit(GLOBAL.SET_LOADING, false)
    },
    setInitialSettingsLoaded({commit}, status){
      commit(GLOBAL.INITIAL_SETTINGS_LOADED, status)
    },
    setBlockDialog({commit}, show){
      commit(GLOBAL.SET_BLOCK_DIALOG, show)
    },
    setBlockDialogTerms({commit}, show){
      commit(GLOBAL.SET_BLOCK_DIALOG_TERMS, show)
    },
    setCorporateNoticeDisplayed({commit}, value){
      commit(GLOBAL.SET_CORPORATE_NOTICE_DISPLAYED, value)
    },
    getCorporateNoticeDisplayed(){
      return this.state.corporateNoticeDisplayed
    },
    setBlockDialogNPS({commit}, show){
      commit(GLOBAL.SET_BLOCK_DIALOG_NPS, show)
    }
  }
}

export default Global
